/**
 * AI React Hooks for InsuraBot Integration
 * Part of PR #135 - Enhanced AI capabilities with React Query integration
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback, useRef, useEffect } from "react";

import type {
  AIContextType,
  AIMessage,
  AIResponse,
  AIAnalytics,
  AIAdminInsights,
  CreateAISessionRequest,
  CreateAISessionResponse,
  SendAIMessageRequest,
  UseAIServiceOptions,
  UseAIServiceReturn,
  UseAIAnalyticsReturn,
  AICapabilities,
  AISessionSummary
} from "@/app/_module/types/ai-types";
import { aiService } from "@/app/_module/services/ai-service";
import { useToast } from "@/app/_module/hooks/use-toast";

/**
 * Enhanced AI service hook with session management
 */
export function useAIService(options: UseAIServiceOptions = {}): UseAIServiceReturn {
  const {
    autoInit = true,
    contextType = "general",
    enableRetry = true,
    maxRetries = 3
  } = options;

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [session, setSession] = useState<CreateAISessionResponse | null>(null);
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const retryCount = useRef(0);

  // Create session mutation
  const createSessionMutation = useMutation({
    mutationFn: (request: CreateAISessionRequest) => aiService.createSession(request),
    onSuccess: (data) => {
      setSession(data);
      setError(null);
      retryCount.current = 0;
    },
    onError: (error: Error) => {
      setError(error.message);
      toast({
        title: "Failed to create AI session",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: (request: SendAIMessageRequest) => aiService.sendMessage(request),
    onSuccess: (response, variables) => {
      const userMessage: AIMessage = {
        id: `user_${Date.now()}`,
        role: "user",
        content: variables.message,
        timestamp: new Date(),
        metadata: variables.metadata
      };

      const assistantMessage: AIMessage = {
        id: `assistant_${Date.now() + 1}`,
        role: "assistant",
        content: response.message,
        timestamp: new Date(),
        metadata: { usage: response.usage, redacted: response.redacted }
      };

      setMessages(prev => [...prev, userMessage, assistantMessage]);
      setError(null);
      retryCount.current = 0;

      // Invalidate analytics queries to refresh data
      void queryClient.invalidateQueries({ queryKey: ["ai", "analytics"] });
    },
    onError: (error: Error) => {
      setError(error.message);
      
      // Handle rate limiting with retry
      if (error.message.includes("Rate limit") && enableRetry && retryCount.current < maxRetries) {
        retryCount.current += 1;
        setTimeout(() => {
          sendMessageMutation.mutate(sendMessageMutation.variables!);
        }, 2000 * retryCount.current);
        return;
      }

      toast({
        title: "Failed to send message",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // End session mutation
  const endSessionMutation = useMutation({
    mutationFn: (sessionId: string) => aiService.endSession(sessionId),
    onSuccess: () => {
      setSession(null);
      setMessages([]);
      setError(null);
    }
  });

  // Load history when session is created
  useEffect(() => {
    if (session?.sessionId) {
      aiService.getHistory(session.sessionId)
        .then(history => {
          setMessages(history);
        })
        .catch(error => {

        });
    }
  }, [session?.sessionId]);

  // Auto-initialize session
  useEffect(() => {
    if (autoInit && !session && !createSessionMutation.isPending) {
      createSessionMutation.mutate({ contextType });
    }
  }, [autoInit, session, contextType, createSessionMutation]);

  const sendMessage = useCallback(async (
    message: string, 
    options?: { metadata?: Record<string, unknown> }
  ): Promise<AIResponse> => {
    if (!session?.sessionId) {
      throw new Error("No active session. Please create a session first.");
    }

    const request: SendAIMessageRequest = {
      message: message.trim(),
      sessionId: session.sessionId,
      contextType,
      metadata: options?.metadata
    };

    return new Promise((resolve, reject) => {
      sendMessageMutation.mutate(request, {
        onSuccess: (data) => resolve(data),
        onError: (error) => reject(error)
      });
    });
  }, [session?.sessionId, contextType, sendMessageMutation]);

  const createSession = useCallback(async (config: CreateAISessionRequest): Promise<CreateAISessionResponse> => {
    return new Promise((resolve, reject) => {
      createSessionMutation.mutate(config, {
        onSuccess: (data) => resolve(data),
        onError: (error) => reject(error)
      });
    });
  }, [createSessionMutation]);

  const endSession = useCallback(async (): Promise<void> => {
    if (!session?.sessionId) return;
    
    return new Promise((resolve, reject) => {
      endSessionMutation.mutate(session.sessionId, {
        onSuccess: () => resolve(),
        onError: (error) => reject(error)
      });
    });
  }, [session?.sessionId, endSessionMutation]);

  const clearHistory = useCallback(() => {
    setMessages([]);
    aiService.clearCache();
  }, []);

  const retryLastMessage = useCallback(async (): Promise<void> => {
    const lastUserMessage = messages.findLast(msg => msg.role === "user");
    if (lastUserMessage && session?.sessionId) {
      await sendMessage(lastUserMessage.content);
    }
  }, [messages, session?.sessionId, sendMessage]);

  return {
    session,
    messages,
    isLoading: createSessionMutation.isPending || sendMessageMutation.isPending,
    error,
    sendMessage,
    createSession,
    endSession,
    clearHistory,
    retryLastMessage
  };
}

/**
 * AI Analytics hook for admin dashboard
 */
export function useAIAnalytics(): UseAIAnalyticsReturn {
  const { toast } = useToast();

  // Analytics query
  const analyticsQuery = useQuery({
    queryKey: ["ai", "analytics"],
    queryFn: () => aiService.getAnalytics(),
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
    retry: 2,
    retryDelay: 1000
  });

  // Admin insights query
  const insightsQuery = useQuery({
    queryKey: ["ai", "admin-insights"],
    queryFn: () => aiService.getAdminInsights(),
    refetchInterval: 10 * 60 * 1000, // Refresh every 10 minutes
    retry: 2,
    retryDelay: 1000
  });

  const refreshAnalytics = useCallback(async () => {
    try {
      await analyticsQuery.refetch();
    } catch (error) {
      toast({
        title: "Failed to refresh analytics",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    }
  }, [analyticsQuery, toast]);

  const refreshInsights = useCallback(async () => {
    try {
      await insightsQuery.refetch();
    } catch (error) {
      toast({
        title: "Failed to refresh insights",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    }
  }, [insightsQuery, toast]);

  return {
    analytics: analyticsQuery.data || null,
    adminInsights: insightsQuery.data || null,
    isLoading: analyticsQuery.isLoading || insightsQuery.isLoading,
    error: analyticsQuery.error?.message || insightsQuery.error?.message || null,
    refreshAnalytics,
    refreshInsights
  };
}

/**
 * AI Capabilities hook
 */
export function useAICapabilities() {
  return useQuery({
    queryKey: ["ai", "capabilities"],
    queryFn: () => aiService.getCapabilities(),
    staleTime: 15 * 60 * 1000, // 15 minutes
    retry: 1
  });
}

/**
 * AI Sessions hook for session management
 */
export function useAISessions(limit = 20) {
  return useQuery({
    queryKey: ["ai", "sessions", limit],
    queryFn: () => aiService.listSessions(limit),
    refetchInterval: 30 * 1000, // Refresh every 30 seconds
    retry: 2
  });
}

/**
 * Enhanced InsuraBot hook with admin context awareness
 */
export function useInsuraBot(options: { 
  enableAdminContext?: boolean;
  contextType?: AIContextType;
} = {}) {
  const { enableAdminContext = false, contextType = "general" } = options;
  
  const [isOpen, setIsOpen] = useState(false);
  const [currentContext, setCurrentContext] = useState<AIContextType>(
    enableAdminContext ? "admin_analytics" : contextType
  );

  const aiServiceHook = useAIService({
    autoInit: false,
    contextType: currentContext
  });

  const analyticsHook = useAIAnalytics();

  // Initialize session when bot opens
  useEffect(() => {
    if (isOpen && !aiServiceHook.session) {
      aiServiceHook.createSession({ contextType: currentContext });
    }
  }, [isOpen, aiServiceHook.session, currentContext, aiServiceHook]);

  const switchContext = useCallback((newContext: AIContextType) => {
    setCurrentContext(newContext);
    // End current session and create new one with different context
    if (aiServiceHook.session) {
      aiServiceHook.endSession().then(() => {
        aiServiceHook.createSession({ contextType: newContext });
      });
    }
  }, [aiServiceHook]);

  return {
    isOpen,
    setIsOpen,
    currentContext,
    switchContext,
    ...aiServiceHook,
    analytics: analyticsHook.analytics,
    adminInsights: analyticsHook.adminInsights,
    isAnalyticsLoading: analyticsHook.isLoading
  };
}